
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { setUser, setAuthTokens } from '../lib/storage';
import Modal from '../components/ui/Modal';
import { BrandFooter } from '../components/ui/BrandFooter';
import { BrandMark } from '../components/ui/BrandMark';
import { useProductName } from '../context/BrandingContext';
import { Button } from '../components/ui/Button';
import { SelectField, TextField } from '../components/ui/Field';

interface SignupPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  username: string;
  department?: 'RCS' | 'ADAM';
}

const Signup: React.FC = () => {
  const productName = useProductName();
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [form, setForm] = useState<SignupPayload>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    username: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPostSignupModal, setShowPostSignupModal] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [modalDepartment, setModalDepartment] = useState<'RCS' | 'ADAM' | ''>('');
  const [signupResponse, setSignupResponse] = useState<any>(null);

  function update<K extends keyof SignupPayload>(key: K, value: SignupPayload[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res: any = await signup(form);

      setSignupResponse(res);

      // setShowPostSignupModal(true);

      toast.success(res?.message || 'Account created. Please complete organization details.');
    } catch (err: any) {
      console.log('Signup error', err);
      const msg = err?.message || 'Failed to create account';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalSubmit = async () => {
    if (!orgName.trim()) {
      toast.error('Organization is required');
      return;
    }
    if (!modalDepartment) {
      toast.error('Please select a department');
      return;
    }

    try {
      if (signupResponse?.user) {
        setUser(signupResponse.user);
      }

      if (signupResponse?.token) {
        setAuthTokens(signupResponse.token);
      } else if (signupResponse?.accessToken) {
        setAuthTokens(signupResponse.accessToken);
      }



      toast.success('Organization and department saved — all set!');
      setShowPostSignupModal(false);
      navigate('/login');
    } catch (err: any) {
      console.error('Failed to finalize profile', err);
      toast.error(err?.message || 'Failed to save organization details');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left Panel - Product Story */}
        <div className="relative hidden lg:flex">
          <div className="relative flex w-full flex-col overflow-hidden bg-gradient-to-br from-c-2b2458 via-c-4b63c9 to-c-3c95d9 p-10 text-white">
            <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-c-9e8bff/35 blur-3xl" />
            <div className="absolute -bottom-28 right-[-60px] h-96 w-96 rounded-full bg-c-59c6f9/25 blur-3xl" />
            <div className="absolute right-16 top-20 h-40 w-40 rounded-full border border-white/20" />

            <div className="relative flex h-full flex-col">
              <div>
                <p className="inline-flex rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-white/90">
                  {productName ? `${productName} Control Layer` : 'Control Layer'}
                </p>
                <h2 className="mt-6 max-w-xl text-[2.4rem] font-semibold leading-[1.1]">
                  Build, monitor, and scale autonomous AI operations
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/85">
                  {productName || 'The platform'} unifies AI agents, workflows, analytics, and governance so teams can ship production-grade automation faster.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3">Live Agent Health</div>
                <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3">Workflow Orchestration</div>
                <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3">Secure Multi-tenant Ops</div>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-xs font-semibold tracking-[0.12em] text-white/80">ACTIVE AGENTS</p>
                  <p className="mt-2 text-4xl font-semibold leading-none">148</p>
                  <p className="mt-2 text-sm text-white/80">Across support, onboarding, and operations</p>
                </div>
                <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-xs font-semibold tracking-[0.12em] text-white/80">TASKS AUTOMATED</p>
                  <p className="mt-2 text-4xl font-semibold leading-none">26.8K</p>
                  <p className="mt-2 text-sm text-white/80">In the last 30 days</p>
                </div>
              </div>

              <div className="relative mt-8 rounded-2xl border border-white/20 bg-c-1f204d/40 p-5 backdrop-blur-sm">
                <p className="text-xs font-semibold tracking-[0.12em] text-white/70">OPERATION SNAPSHOT</p>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
                    <span>Intent Classification</span>
                    <span className="font-semibold text-[#A9F6C8]">99.2%</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
                    <span>Response SLA</span>
                    <span className="font-semibold text-c-7fe3ff">1.8s avg</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
                    <span>Escalation Accuracy</span>
                    <span className="font-semibold text-c-d6b8ff">97.6%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Signup Form */}
        <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
          <div className="w-full max-w-md">
            <div className="flex items-center mb-6">
              <BrandMark className="h-16 w-auto" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              {productName ? `Create your ${productName} account` : 'Create your account'}
            </h1>
            <p className="text-gray-600 mb-8">Sign up to get started — basic account details first.</p>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <label className="flex flex-col text-sm">
                  <span className="text-gray-600 text-xs font-medium">First name</span>
                  <TextField
                    required
                    value={form.firstName}
                    onChange={e => update('firstName', e.target.value)}
                    className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                    placeholder="John"
                  />
                </label>

                <label className="flex flex-col text-sm">
                  <span className="text-gray-600 text-xs font-medium">Last name</span>
                  <TextField
                    required
                    value={form.lastName}
                    onChange={e => update('lastName', e.target.value)}
                    className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                    placeholder="Doe"
                  />
                </label>
              </div>

              <label className="flex flex-col text-sm">
                <span className="text-gray-600 text-xs font-medium">Username</span>
                <TextField
                  required
                  value={form.username}
                  onChange={e => update('username', e.target.value)}
                  className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                  placeholder="johndoe"
                />
              </label>

              <label className="flex flex-col text-sm">
                <span className="text-gray-600 text-xs font-medium">Email</span>
                <TextField
                  required
                  type="email"
                  value={form.email}
                  onChange={e => update('email', e.target.value)}
                  className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                  placeholder="you@company.com"
                />
              </label>

              <div className="flex items-center gap-3">
                <label className="flex-1 flex flex-col text-sm">
                  <span className="text-gray-600 text-xs font-medium">Password</span>
                  <div className="relative mt-1">
                    <TextField
                      required
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => update('password', e.target.value)}
                      className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter a strong password"
                    />
                    <Button tone="custom"
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      className="absolute right-2 top-2 text-xs text-gray-500 px-2 py-1"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                </label>
              </div>

              {/* Role selector moved to modal - show a note here */}
              {/* <div className="text-sm">
                <div className="text-xs text-gray-500 font-semibold mb-2">Choose role</div>
                <div className="text-xs text-gray-500 mb-2">(You will choose department after creating your account)</div>
              </div> */}

              <div className="pt-2">
                <Button tone="custom"
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-[30px] border-0 bg-gradient-to-r from-c-805dc6 to-c-3683c6 py-3 text-white font-semibold shadow-none disabled:opacity-60"
                >
                  {submitting ? 'Creating account...' : 'Create account'}
                </Button>
              </div>

              <div className="text-center text-sm text-gray-500">
                By creating an account you agree to our <span className="underline">Terms</span> and <span className="underline">Privacy Policy</span>.
              </div>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              Already have an account? <Button tone="custom" onClick={() => navigate('/login')} className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark font-medium">Sign in</Button>
            </div>

            <BrandFooter />
          </div>
        </div>
      </div>

      {/* Post-signup modal */}
      <Modal
        isOpen={showPostSignupModal}
        onClose={() => {
          return;
        }}
        title="Complete Organization Details"
        size="md"
        showCloseButton={false}
        closeOnBackdropClick={false}
        closeOnEsc={false}
      >
        <div>
          <p className="text-sm text-gray-600 mb-4">Please provide your organization and select your department to finish setup.</p>

          <label className="flex flex-col text-sm mb-3">
            <span className="text-gray-600 text-xs font-medium">Organization</span>
            <TextField
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
              placeholder="Your organization name"
            />
          </label>

          <label className="flex flex-col text-sm mb-6">
            <span className="text-gray-600 text-xs font-medium">Department</span>
            <SelectField
              value={modalDepartment}
              onChange={e => setModalDepartment(e.target.value as 'RCS' | 'ADAM' | '')}
              className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Select department</option>
              <option value="RCS">RCS — Agent orchestration & routing</option>
              <option value="ADAM">ADAM — Autonomous decision automation</option>
            </SelectField>
          </label>

          <div className="flex justify-end gap-3">
            <Button tone="primary"
              onClick={handleModalSubmit}
              className="px-4 py-2 rounded-lg font-semibold"
            >
              Save & Continue
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Signup;
